import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setSubmitting(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d79q66b2hsar0k5m0rb0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#111827]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-teal-400 font-semibold mb-4">
            Get in Touch
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Contact Us
          </h2>
          <p className="text-white/60 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            Have questions about our iOS applications? We&apos;re here to help.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          {/* Left: contact info */}
          <div className="flex-1 flex flex-col gap-8">
            {/* Address */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-600/20 shrink-0">
                <i className="ri-map-pin-line text-teal-400 text-lg" />
              </div>
              <div>
                <p className="text-white/50 text-xs uppercase tracking-wider mb-2 font-medium">Address</p>
                <p className="text-white/90 text-sm leading-relaxed">
                  Zrinsko-Frankopanska ulica 38a<br />
                  Zadar, 23000<br />
                  Croatia
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-600/20 shrink-0">
                <i className="ri-mail-line text-teal-400 text-lg" />
              </div>
              <div>
                <p className="text-white/50 text-xs uppercase tracking-wider mb-2 font-medium">Email</p>
                <p className="text-white/90 text-sm">help@konusdoo.com</p>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-600/20 shrink-0">
                <i className="ri-phone-line text-teal-400 text-lg" />
              </div>
              <div>
                <p className="text-white/50 text-xs uppercase tracking-wider mb-2 font-medium">Phone</p>
                <p className="text-white/90 text-sm">+1 7405549331</p>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-white/10 pt-8">
              <p className="text-white/40 text-xs leading-relaxed">
                KONUS d.o.o. — Developing modern iOS applications for enhanced security and performance.
              </p>
            </div>
          </div>

          {/* Right: contact form */}
          <div className="flex-1">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-teal-600/20 mb-5">
                  <i className="ri-check-line text-teal-400 text-2xl" />
                </div>
                <h3 className="text-white text-xl font-semibold mb-2">Message Sent!</h3>
                <p className="text-white/60 text-sm">Thank you for reaching out. We&apos;ll get back to you shortly.</p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <div className="flex flex-col sm:flex-row gap-5">
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="text-white/50 text-xs uppercase tracking-wider font-medium">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Your name"
                      className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-teal-500 transition-colors"
                    />
                  </div>
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="text-white/50 text-xs uppercase tracking-wider font-medium">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-teal-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-white/50 text-xs uppercase tracking-wider font-medium">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    required
                    placeholder="How can we help?"
                    className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-teal-500 transition-colors"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-white/50 text-xs uppercase tracking-wider font-medium flex justify-between">
                    <span>Message</span>
                    <span className={charCount > 500 ? 'text-red-400' : 'text-white/30'}>{charCount}/500</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    placeholder="Tell us more..."
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-teal-500 transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting || charCount > 500}
                  className="bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white font-medium text-sm rounded-lg px-6 py-3 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer bar */}
      <div className="border-t border-white/10 mt-20 pt-8 text-center">
        <p className="text-white/30 text-xs">
          &copy; {new Date().getFullYear()} KONUS d.o.o. All rights reserved.
        </p>
      </div>
    </section>
  );
}
